import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tagline } from "@/components/pro-blocks/landing-page/tagline"
import { Tag, AlertCircle, Link2, DollarSign, Bell, Mail, Search, Globe, Users, Send, Lock, Brain } from "lucide-react"
import { EmailAnalysisDemo } from "@/components/feedbackiq/email-analysis-demo"
import { WeeklyDigestDemo } from "@/components/feedbackiq/weekly-digest-demo"
import { ChatStreamDemo } from "@/components/feedbackiq/chat-stream-demo"

const analysisSteps = [
  {
    icon: Tag,
    title: "Определение области продукта",
    description:
      "Система понимает, к какой части продукта относится обращение: оформление заказа, оплата, настройки и др.",
  },
  {
    icon: AlertCircle,
    title: "Оценка критичности",
    description: "На основе текста определяется уровень срочности: критический, высокий, средний или низкий.",
  },
  {
    icon: Link2,
    title: "Связывание похожих обращений",
    description: "10 клиентов написали про одну проблему разными словами — система объединит в один паттерн.",
  },
  {
    icon: DollarSign,
    title: "Расчёт бизнес-влияния",
    description: "Сколько клиентов затронуто, какой совокупный доход, какой риск потери.",
  },
]

const capabilities = [
  {
    icon: Brain,
    title: "Понимание контекста",
    description: "Различает баги, запросы функций, проблемы UX и вопросы по биллингу",
  },
  {
    icon: Globe,
    title: "Многоязычная поддержка",
    description: "Обращения на разных языках анализируются одинаково хорошо",
  },
  {
    icon: Users,
    title: "Разделение по командам",
    description: "Разные представления для продакта, разработки и саппорта",
  },
  {
    icon: Send,
    title: "Интеграция с трекерами",
    description: "Отправляйте проблемы напрямую в ваш трекер задач",
  },
  {
    icon: Lock,
    title: "Безопасность данных",
    description: "Шифрование, соответствие стандартам, возможность удаления",
  },
  {
    icon: Search,
    title: "Обучение на корректировках",
    description: "Исправьте категорию один раз — система запомнит",
  },
]

export function HowItWorksDetailed() {
  return (
    <section className="bg-background section-padding-y border-b">
      <div className="container-padding-x container mx-auto flex flex-col gap-16">
        {/* Section header */}
        <div className="section-title-gap-lg mx-auto flex max-w-xl flex-col items-center text-center">
          <Tagline>Подробнее</Tagline>
          <h2 className="heading-lg">Как FeedbackIQ анализирует обращения</h2>
        </div>

        {/* Step 2: Analysis demo */}
        <div className="flex flex-col gap-8">
          <div className="flex items-center gap-3">
            <Badge className="bg-primary text-primary-foreground">Шаг 2</Badge>
            <h3 className="text-foreground text-xl font-semibold">Автоматический анализ каждого обращения</h3>
          </div>

          <EmailAnalysisDemo />

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {analysisSteps.map((step, index) => (
              <Card key={index} className="bg-muted/30 border-none p-4 shadow-none">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                    <step.icon className="text-primary h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-foreground mb-1 text-sm font-medium">{step.title}</h4>
                    <p className="text-muted-foreground text-xs">{step.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Step 3: Outputs */}
        <div className="flex flex-col gap-8">
          <div className="flex items-center gap-3">
            <Badge className="bg-primary text-primary-foreground">Шаг 3</Badge>
            <h3 className="text-foreground text-xl font-semibold">Получайте готовые выводы</h3>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Instant notifications */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Bell className="text-primary h-5 w-5" />
                <h4 className="text-foreground font-medium">Мгновенные уведомления</h4>
              </div>
              <ChatStreamDemo />
            </div>

            {/* Weekly digest */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Mail className="text-primary h-5 w-5" />
                <h4 className="text-foreground font-medium">Еженедельный дайджест</h4>
              </div>
              <WeeklyDigestDemo />
            </div>
          </div>
        </div>

        {/* Capabilities */}
        <div className="flex flex-col gap-8">
          <h3 className="text-foreground text-center text-xl font-semibold">Ключевые возможности системы</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {capabilities.map((cap, index) => (
              <Card key={index} className="bg-muted/30 border-none p-5 shadow-none">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                    <cap.icon className="text-primary h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-foreground mb-1 font-medium">{cap.title}</h4>
                    <p className="text-muted-foreground text-sm">{cap.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Typical scenario */}
        <Card className="bg-secondary border-none p-6 shadow-none lg:p-8">
          <h3 className="text-foreground mb-6 text-center text-xl font-semibold">Типичный сценарий использования</h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="text-center">
              <div className="text-primary mb-2 text-sm font-medium">Понедельник, 9:00</div>
              <p className="text-muted-foreground text-sm">
                Открываете FeedbackIQ и видите еженедельный дайджест: 312 обращений, 5 критических проблем
              </p>
            </div>
            <div className="text-center">
              <div className="text-primary mb-2 text-sm font-medium">9:10</div>
              <p className="text-muted-foreground text-sm">
                Кликаете на главную проблему: 43 клиента, $19K под угрозой, растёт 3 дня
              </p>
            </div>
            <div className="text-center">
              <div className="text-primary mb-2 text-sm font-medium">9:30</div>
              <p className="text-muted-foreground text-sm">
                За 30 минут получили полную картину с количественными оценками и динамикой
              </p>
            </div>
            <div className="text-center">
              <div className="text-primary mb-2 text-sm font-medium">10:00 — Встреча</div>
              <p className="text-muted-foreground text-sm">
                Решение принимается на данных за 5 минут вместо 30-минутной дискуссии
              </p>
            </div>
          </div>
        </Card>
      </div>
    </section>
  )
}
